/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, Component, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IntlTelInputComponent } from 'intl-tel-input/angular';
import { AllOptions } from 'intl-tel-input/angular/build/types/modules/types/public-api';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { Iso2 } from 'intl-tel-input/angular/build/types/intl-tel-input/data';
import da from "intl-tel-input/i18n/da";
import sv from "intl-tel-input/i18n/sv";
import en from "intl-tel-input/i18n/en";
import { TranslationService } from '../../data/translation.service';


@Component({
  selector: 'app-phone-input',
  imports: [CommonModule, IntlTelInputComponent],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss',
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => PhoneInputComponent),
        multi: true,
      },
      {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    ]
})
export class PhoneInputComponent implements AfterViewInit, ControlValueAccessor {
  @Input() disabled = false;

  @Output() dropdownOpenChange = new EventEmitter<boolean>(false);

  @ViewChild('phoneInput') phoneInput!: IntlTelInputComponent;

  value = '';
  isValid = false;
  priorityOrder: Iso2[] = [
    'dk', 'gb', 'us', 'pl', 'de', 'tw', 'se', 'nl',
    'ie', 'gr', 'no', 'ac', 'ro', 'ch', 'sk', 'fi',
    'cz', 'es', 'be', 'ru', 'lv', 'lt', 'hr', 'fr',
    'pt', 'bg', 'ua', 'it', 'ph',
  ];

  itiOptions: Partial<AllOptions>;

  private pendingValue: string | null = null;

  private onChange = (_: string): void => {};
  private onTouched = (): void => {};

  constructor(private translationService: TranslationService) {
    const i18n = this.getI18nLocale();

    this.itiOptions = {
    i18n: i18n,
    initialCountry: 'dk',
    separateDialCode: true,
    autoPlaceholder: 'polite',
    placeholderNumberType: 'MOBILE',
    countryOrder: this.priorityOrder,
    loadUtils: (): any => import('intl-tel-input/build/js/utils.js')
      .then((mod) => {
        return { default: mod.default ?? mod } as any;
      })
    };
  }

  ngAfterViewInit(): void {
    if (this.pendingValue !== null) {
      this.phoneInput?.writeValue(this.pendingValue);
      this.pendingValue = null;
    }

    const input: HTMLInputElement | null = this.phoneInput?.inputRef.nativeElement;

    if (input) {
      setTimeout(() => input.focus(), 1);

      input.addEventListener('open:countrydropdown', () => {
        this.dropdownOpenChange.emit(true);
      });

      input.addEventListener('close:countrydropdown', () => {
        this.dropdownOpenChange.emit(false);
      });
    }
  }

  onNumberChange(): void {
    const instance = this.phoneInput?.getInstance();
    const phoneNumber = this.phoneInput?.inputRef.nativeElement.value;
    const country = instance?.getSelectedCountryData();
    const number = `${country?.dialCodePlus}${phoneNumber}`;
    const valid = instance?.isValidNumber();

    this.isValid = !!valid;
    this.value = number;
    this.onChange(number);
  }

  onValidityChange(valid: boolean): void {
    this.isValid = valid;
  }

  writeValue(value: string): void {
    this.value = value || '';

    if (this.phoneInput) {
      this.phoneInput?.writeValue(this.value);
    } else {
      this.pendingValue = value;
    }

    this.onChange(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.phoneInput?.setDisabledState(this.disabled);
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.isValid ? null : { invalidPhoneNumber: true };
  }

getI18nLocale(): { [key: string]: any } {
  const locales: Record<string, any> = {
    en,
    dk: da,
    se: sv,
  };

  const code = this.translationService.getLanguageCode();

  return locales[code] || en;
}

}
